import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API } from '../config/api';
import { setGlobalAuthErrorHandler } from '../utils/api';
import useEducationStore from '../stores/educationStore';
import { revokeReaderDevice } from '../utils/readerApi';

// Multi-account model.
//
// One browser can hold sessions for several accounts at once (family
// scenario: mom + kids). The backend keeps a set of httpOnly cookies keyed by
// userId, plus a small JS-readable `lingvish_active_user` cookie telling it
// which session to authenticate the current request against.
//
// On the client we keep a mirrored list of the LOGGED-IN user profiles in
// LocalStorage under `lingvish_sessions` so we can render the account
// switcher without re-fetching. The active user is derived from the
// `lingvish_active_user` cookie so the client and server never disagree
// about who the request is for.
const SESSIONS_STORAGE_KEY = 'lingvish_sessions';
const LEGACY_USER_STORAGE_KEY = 'lingvish_user';
const PREFS_STORAGE_KEY = 'lingvish_prefs';
const ACTIVE_USER_COOKIE = 'lingvish_active_user';

// Transform user data from backend (snake_case) to frontend (camelCase)
function transformUser(user) {
  if (!user) return user;
  return {
    ...user,
    skillLevels: user.skill_levels || user.skillLevels,
    levelTestCompleted: user.level_test_completed ?? user.levelTestCompleted,
    // Admin-specific fields
    adminPermissions: user.admin_permissions || user.adminPermissions || [],
    isSuperAdmin: user.is_super_admin ?? user.isSuperAdmin ?? false,
  };
}

function readCookie(name) {
  if (typeof document === 'undefined') return null;
  const prefix = `${name}=`;
  const parts = document.cookie ? document.cookie.split('; ') : [];
  for (const part of parts) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function loadSessions() {
  // New multi-account storage.
  try {
    const raw = localStorage.getItem(SESSIONS_STORAGE_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.map(transformUser).filter(Boolean);
    }
  } catch { /* ignore */ }

  // One-time migration from the pre-multi-account single-user key.
  try {
    const raw = localStorage.getItem(LEGACY_USER_STORAGE_KEY);
    if (raw) {
      const u = transformUser(JSON.parse(raw));
      if (u && u.id) {
        localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify([u]));
        return [u];
      }
    }
  } catch { /* ignore */ }

  return [];
}

function saveSessions(sessions) {
  try {
    localStorage.setItem(SESSIONS_STORAGE_KEY, JSON.stringify(sessions));
  } catch { /* ignore */ }
}

const SUPPORTED_LANGUAGES = ['uk', 'en'];
const DEFAULT_LANGUAGE = 'uk';

function loadPrefs() {
  try {
    const raw = localStorage.getItem(PREFS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      const language = SUPPORTED_LANGUAGES.includes(parsed?.language)
        ? parsed.language
        : DEFAULT_LANGUAGE;
      return { darkMode: !!parsed?.darkMode, language };
    }
  } catch { /* ignore */ }
  return { darkMode: false, language: DEFAULT_LANGUAGE };
}

function savePrefs(prefs) {
  try {
    localStorage.setItem(PREFS_STORAGE_KEY, JSON.stringify(prefs));
  } catch { /* ignore */ }
}

function pickActive(sessions, preferredId) {
  if (!sessions || sessions.length === 0) return null;
  if (preferredId) {
    const match = sessions.find(s => s && s.id === preferredId);
    if (match) return match;
  }
  return sessions[0];
}

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [sessions, setSessions] = useState(loadSessions);
  const [activeUserId, setActiveUserId] = useState(() => readCookie(ACTIVE_USER_COOKIE));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const prefs = loadPrefs();
  const [darkMode, setDarkModeState] = useState(prefs.darkMode);
  const [language, setLanguageState] = useState(prefs.language);

  const user = pickActive(sessions, activeUserId);
  const userRole = user?.role ?? null;

  // ------- Session list helpers -------
  const upsertSession = useCallback((incomingUser) => {
    const transformed = transformUser(incomingUser);
    if (!transformed || !transformed.id) return;
    // If we're switching to a different active account (e.g. add-account
    // flow while an admin was already logged in), drop cached data so the
    // new user never sees the previous account's rows flash on mount.
    if (activeUserId && activeUserId !== transformed.id) {
      useEducationStore.getState().resetStore();
    }
    setSessions(prev => {
      const others = (prev || []).filter(s => s && s.id !== transformed.id);
      const next = [...others, transformed];
      saveSessions(next);
      return next;
    });
    setActiveUserId(transformed.id);
    setIsAuthenticated(true);
  }, [activeUserId]);

  const removeSession = useCallback((userId) => {
    setSessions(prev => {
      const next = (prev || []).filter(s => s && s.id !== userId);
      saveSessions(next);
      // If the removed session was active, promote the next one (if any).
      if (userId === activeUserId) {
        const nextActive = next[0]?.id || null;
        setActiveUserId(nextActive);
        setIsAuthenticated(!!nextActive);
      }
      return next;
    });
  }, [activeUserId]);

  const clearAllSessions = useCallback(() => {
    try {
      localStorage.removeItem(SESSIONS_STORAGE_KEY);
      localStorage.removeItem(LEGACY_USER_STORAGE_KEY);
    } catch { /* ignore */ }
    setSessions([]);
    setActiveUserId(null);
    setIsAuthenticated(false);
  }, []);

  // ------- Auth actions -------

  // Refresh access token using the httpOnly refresh cookie for the currently
  // active account. On success returns true; on kicked-session returns false
  // and evicts the account from local state.
  const refreshAccessToken = useCallback(async () => {
    try {
      const res = await fetch(`${API.AUTH}/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!res.ok) {
        // The active account's session is dead. Drop just that one; other
        // accounts on this browser stay logged in.
        if (activeUserId) removeSession(activeUserId);
        return false;
      }

      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Token refresh failed:', error);
      return false;
    }
  }, [activeUserId, removeSession]);

  // Authenticated fetch — cookies do the auth, retry once on 401.
  const authFetch = useCallback(async (url, options = {}) => {
    const makeRequest = async (isRetry = false) => {
      const headers = {
        ...options.headers,
        'Content-Type': 'application/json',
      };

      const res = await fetch(url, {
        ...options,
        credentials: 'include',
        headers,
      });

      if (res.status === 401 && !isRetry) {
        const refreshed = await refreshAccessToken();
        if (refreshed) return makeRequest(true);
        throw new Error('Authentication failed');
      }

      return res;
    };

    return makeRequest();
  }, [refreshAccessToken]);

  // Login — server sets per-user httpOnly cookies + active-user marker.
  const login = useCallback(async (email, password) => {
    const res = await fetch(`${API.AUTH}/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Login failed');
    }

    const transformedUser = transformUser(data.user);
    upsertSession(transformedUser);

    return { ...data, user: transformedUser };
  }, [upsertSession]);

  const loginWithGoogle = useCallback(async (credential) => {
    const res = await fetch(`${API.AUTH}/google`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ credential }),
    });

    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.error || 'Google login failed');
    }

    const transformedUser = transformUser(data.user);
    upsertSession(transformedUser);

    return { ...data, user: transformedUser };
  }, [upsertSession]);

  // Logout the CURRENT active account. Other accounts stay signed in.
  // Returns the next active userId (or null if none).
  const handleLogout = useCallback(async () => {
    const loggingOutId = activeUserId;
    try {
      await fetch(`${API.AUTH}/logout`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
    } catch { /* ignore */ }

    // Also revoke the Reader device bearer for THIS browser. The device token
    // is intentionally long-lived (survives cookie expiry / single-session
    // kicks so an installed PWA keeps working), but an EXPLICIT logout means
    // the user wants a clean state — otherwise they'd hit the Reader anon flow
    // and still see full content because the bearer is honored.
    try { await revokeReaderDevice(); } catch { /* ignore */ }

    // Drop cached data from the previous account so the next active user
    // never sees stale rows during the initial render.
    useEducationStore.getState().resetStore();

    // Compute what stays after removing the logged-out account.
    const remaining = (sessions || []).filter(s => s && s.id !== loggingOutId);
    saveSessions(remaining);
    setSessions(remaining);

    if (remaining.length > 0) {
      // Switch to another logged-in account on the server, then reflect it.
      const nextId = remaining[0].id;
      try {
        const res = await fetch(`${API.AUTH}/switch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ userId: nextId }),
        });
        if (res.ok) {
          setActiveUserId(nextId);
          setIsAuthenticated(true);
          return nextId;
        }
        // Backend has no cookie for this "remaining" account (e.g. it was
        // kicked from another device meanwhile). Drop it locally and try the
        // next one on the next tick — for now, fall through to signed-out.
        const stillRemaining = remaining.filter(s => s.id !== nextId);
        saveSessions(stillRemaining);
        setSessions(stillRemaining);
      } catch { /* fall through */ }
    }

    setActiveUserId(null);
    setIsAuthenticated(false);
    return null;
  }, [activeUserId, sessions]);

  // Parent-only: mint a session for a child without needing their password.
  // Backend verifies the child's parent_id matches the authenticated caller.
  // On success the child is added to the local session list and becomes
  // active — subsequent API calls authenticate as the child.
  const enterChildProfile = useCallback(async (childId) => {
    try {
      const res = await fetch(`${API.AUTH}/login-as-child`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ childId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.user) {
        return { ok: false, error: data?.error || 'Не вдалося увійти в профіль дитини' };
      }
      // Cache child's data as a session and flip active user to the child.
      useEducationStore.getState().resetStore();
      upsertSession(data.user);
      return { ok: true, user: data.user };
    } catch (error) {
      console.error('Enter child profile failed:', error);
      return { ok: false, error: 'Помилка мережі' };
    }
  }, [upsertSession]);

  // Switch to another already-logged-in account (no password needed).
  const switchAccount = useCallback(async (userId) => {
    if (!userId || userId === activeUserId) return false;
    try {
      const res = await fetch(`${API.AUTH}/switch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ userId }),
      });
      if (!res.ok) {
        // No valid backend session for this profile — drop it locally.
        removeSession(userId);
        return false;
      }
      // Drop cached data from the previous account so the new user never
      // sees stale rows before their own data loads.
      useEducationStore.getState().resetStore();
      setActiveUserId(userId);
      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error('Account switch failed:', error);
      return false;
    }
  }, [activeUserId, removeSession]);

  const setUserRole = useCallback((role) => {
    if (!user) return;
    const updated = { ...user, role };
    upsertSession(updated);
  }, [user, upsertSession]);

  const setDarkMode = useCallback((enabled) => {
    setDarkModeState(enabled);
    savePrefs({ darkMode: enabled, language });
  }, [language]);

  const setLanguage = useCallback((next) => {
    const value = SUPPORTED_LANGUAGES.includes(next) ? next : DEFAULT_LANGUAGE;
    setLanguageState(value);
    savePrefs({ darkMode, language: value });
  }, [darkMode]);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('lang', language);
    }
  }, [language]);

  // Set tokens after registration — server has already set cookies, this
  // just mirrors the new user into local state.
  const setTokens = useCallback((accessToken, refreshToken, userData) => {
    const transformedUser = transformUser(userData);
    upsertSession(transformedUser);
  }, [upsertSession]);

  const updateUser = useCallback((updates) => {
    if (!user) return;
    const updated = { ...user, ...updates };
    upsertSession(updated);
  }, [user, upsertSession]);

  // Sync active user from LocalStorage (called after edits from other tabs).
  const syncUserFromStorage = useCallback(() => {
    const stored = loadSessions();
    setSessions(stored);
    const cookieActive = readCookie(ACTIVE_USER_COOKIE);
    const active = pickActive(stored, cookieActive || activeUserId);
    if (active) setActiveUserId(active.id);
    return active || null;
  }, [activeUserId]);

  // Global auth-error hook fired by utils/api.js on unrecoverable 401s.
  useEffect(() => {
    setGlobalAuthErrorHandler((info) => {
      if (info?.reason === 'session_invalidated' && typeof window !== 'undefined') {
        // eslint-disable-next-line no-alert
        window.alert('Ви увійшли з іншого пристрою. Цю сесію завершено.');
      }
      // Evict just the currently-active account. Other family members on this
      // browser stay logged in and the app switches to one of them.
      if (activeUserId) {
        removeSession(activeUserId);
      } else {
        clearAllSessions();
      }
    });

    return () => setGlobalAuthErrorHandler(null);
  }, [activeUserId, removeSession, clearAllSessions]);

  // Check authentication status on mount — hits /me for the currently active
  // account (server picks it based on `lingvish_active_user` cookie).
  //
  // If /me AND /refresh both fail, any sessions in localStorage are stale
  // (cookies were cleared out-of-band, e.g. by the user or by browser
  // privacy tools). Purge them so the UI doesn't paint a logged-in header
  // for an account that can't make requests.
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await fetch(`${API.AUTH}/me`, { credentials: 'include' });

        if (res.ok) {
          const data = await res.json();
          upsertSession(transformUser(data.user));
        } else if (res.status === 401) {
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            const retryRes = await fetch(`${API.AUTH}/me`, { credentials: 'include' });
            if (retryRes.ok) {
              const data = await retryRes.json();
              upsertSession(transformUser(data.user));
            } else {
              clearAllSessions();
            }
          } else {
            clearAllSessions();
          }
        }
      } catch (error) {
        console.error('Auth check failed:', error);
      }
      setIsLoading(false);
    };

    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = {
    user,
    userRole,
    isAuthenticated,
    isLoading,
    darkMode,
    language,
    // Multi-account
    sessions,
    activeUserId,
    switchAccount,
    enterChildProfile,
    // Auth actions
    login,
    loginWithGoogle,
    handleLogout,
    setUserRole,
    setDarkMode,
    setLanguage,
    setTokens,
    updateUser,
    authFetch,
    refreshAccessToken,
    syncUserFromStorage,
    accessToken: undefined, // legacy prop
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
